/**
 * Extracts a still frame from a video File at the given offset (default 50%)
 * and returns it as a JPEG Blob. Used to generate a poster image client-side
 * during upload so the server can store it alongside the video.
 *
 * Long-term, the server should run ffmpeg on upload — this is a useful
 * fallback that produces a "good enough" poster for most posts and stories.
 *
 * @returns Promise<Blob | null>  null on any failure (corrupt video, CORS, etc.)
 */
export async function extractVideoPoster(
  file: File,
  options: { atSeconds?: number; quality?: number; maxWidth?: number } = {}
): Promise<Blob | null> {
  if (!file.type.startsWith('video/')) return null;

  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    let settled = false;
    const done = (blob: Blob | null) => {
      if (settled) return;
      settled = true;
      try { URL.revokeObjectURL(url); } catch {}
      try { video.src = ''; } catch {}
      resolve(blob);
    };

    video.onloadedmetadata = () => {
      const target = options.atSeconds ?? Math.min(video.duration * 0.5, 5);
      video.currentTime = isFinite(target) && target > 0 ? target : 0;
    };

    video.onseeked = () => {
      try {
        const maxW = options.maxWidth ?? 1080;
        const scale = Math.min(1, maxW / (video.videoWidth || maxW));
        const w = Math.max(1, Math.round((video.videoWidth || 640) * scale));
        const h = Math.max(1, Math.round((video.videoHeight || 360) * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return done(null);
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob(b => done(b), 'image/jpeg', options.quality ?? 0.85);
      } catch {
        done(null);
      }
    };

    video.onerror = () => done(null);

    // Timeout safety net — if the video stalls, bail rather than block upload.
    setTimeout(() => done(null), 10000);

    video.src = url;
  });
}
